"use client"
import React, { useState } from "react"
import {
  Plus,
  Minus,
  X,
  Trash2,
  CircleHelp
} from "lucide-react"
import { createPortal } from "react-dom"
import MarkdownToolbarEditor from "../markdown/MarkdownToolbarEditor"
import IconPicker from "../ui/IconPicker"
import { ICON_GROUPS, resolveBlockIcon, suggestIconForName } from "../AvatarsView/lib/blockIcons"
import { nanoid } from "nanoid"

export default function SectionsManager({ 
  sections, 
  setSections, 
  title = "Sections",
  description = "Customize how your agent responds by setting specific guidelines",
  addButtonLabel = "Add Section",
  deleteButtonLabel = "Delete Last",
  emptyLabel = "No sections yet.",
  emptySubLabel = "Click 'Add' to create your first section",
  placeholder = "Write the content for this section...",
  autoTitle = false, // Nuevo prop para generar títulos automáticos
  titlePrefix = "Section", // Prefijo para los títulos automáticos
  isExample = false, // Solo cambia el icono por defecto que se ofrece
  helperTip = "Use sections to define tone, boundaries, and how the avatar should answer.",
  starterSections = []
}) {
  const [activeOverlay, setActiveOverlay] = useState(null)
  const [tempSection, setTempSection] = useState(null)
  const [validationError, setValidationError] = useState("")

  const hasStarterSections = Array.isArray(starterSections) && starterSections.length > 0

  const openStarterSection = (starter) => {
    if (!starter?.title) return

    const id = nanoid()
    const markdown = starter.markdown || ""
    const html = starter.html || ""
    const content = starter.content || markdown

    const newSection = {
      id,
      title: starter.title,
      html,
      markdown,
      content,
    }

    setTempSection(newSection)
    setActiveOverlay(id)
    setValidationError("")
  }

  const addSection = () => {
    const id = nanoid()
    let newTitle = ""
    
    // Si autoTitle está habilitado, generar título automático
    if (autoTitle) {
      const nextNumber = sections.length + 1
      newTitle = `${titlePrefix} ${nextNumber}`
    }
    
    const newSection = { id, title: newTitle, html: "", markdown: "" }
    setTempSection(newSection)
    setActiveOverlay(id)
    setValidationError("")
  }

  const deleteLastSection = () => {
    if (!sections.length) return
    const last = sections[sections.length - 1]
    setSections(prev => prev.slice(0, -1))
    if (activeOverlay === last.id) setActiveOverlay(null)
  }

  const removeSection = (sectionId) => {
    setSections(prev => prev.filter(x => x.id !== sectionId))
    if (activeOverlay === sectionId) {
      setActiveOverlay(null)
      setTempSection(null)
      setValidationError("")
    }
  }

  const updateTempSection = (updates) => {
    if (tempSection) {
      setTempSection(prev => ({ ...prev, ...updates }))
    } else {
      // If editing existing section
      setSections(prev => 
        prev.map(x => x.id === activeOverlay ? { ...x, ...updates } : x)
      )
    }
  }

  const handleCloseOverlay = () => {
    setActiveOverlay(null)
    setTempSection(null)
    setValidationError("")
  }

  const handleSaveSection = () => {
    const currentSection = tempSection || sections.find(x => x.id === activeOverlay)
    
    // Si autoTitle está deshabilitado, validar que el título no esté vacío
    if (!autoTitle && (!currentSection?.title || currentSection.title.trim() === '')) {
      setValidationError("Title is required. Please enter a title for this section.")
      return
    }
    
    // Si autoTitle está habilitado y el título está vacío, generar uno automático
    if (autoTitle && (!currentSection?.title || currentSection.title.trim() === '')) {
      const nextNumber = sections.length + 1
      currentSection.title = `${titlePrefix} ${nextNumber}`
    }

    if (tempSection) {
      // Adding new section
      setSections(prev => [...prev, currentSection])
    }
    // If editing existing section, changes are already saved via updateTempSection
    
    setActiveOverlay(null)
    setTempSection(null)
    setValidationError("")
  }

  const handleEditExistingSection = (sectionId) => {
    setActiveOverlay(sectionId)
    setTempSection(null)
    setValidationError("")
  }

  return (
    <div className="relative h-full min-h-[400px]">
      {/* Sections UI */}
      <div className={`h-full ${activeOverlay ? "opacity-0 pointer-events-none" : ""}`}>
        <div className="h-full">
          <div className="mb-2 flex items-center gap-2">
            <p className="text-white text-sm sm:text-base">{title}</p>
            <div className="group relative inline-flex">
              <button
                type="button"
                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-white/20 bg-white/5 text-gray-300 hover:text-white"
                aria-label="Sections help"
              >
                <CircleHelp className="h-3.5 w-3.5" />
              </button>
              <div className="pointer-events-none absolute left-0 top-full z-20 mt-2 hidden w-72 rounded-lg border border-white/20 bg-black/90 px-3 py-2 text-xs text-gray-200 shadow-xl group-hover:block">
                {helperTip}
              </div>
            </div>
          </div>
          <p className="text-gray-300 text-xs sm:text-sm mb-4">{description}</p>

          {hasStarterSections && sections.length === 0 && (
            <div className="mb-4 rounded-lg border border-white/10 bg-white/5 p-3">
              <p className="mb-2 text-xs text-gray-300">Not sure what to write? Start with one of these:</p>
              <div className="flex flex-wrap gap-2">
                {starterSections.map((starter) => (
                  <button
                    key={starter.title}
                    type="button"
                    onClick={() => openStarterSection(starter)}
                    className="inline-flex items-center gap-1 rounded-full border border-purple-400/30 bg-purple-600/10 px-3 py-1 text-xs text-purple-200 hover:bg-purple-600/20"
                  >
                    <Plus className="h-3 w-3" />
                    {starter.title}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex gap-3 mb-6">
            <button
              onClick={addSection}
              className="bg-primary hover:bg-violet-700 text-white text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-lg inline-flex items-center gap-1.5 whitespace-nowrap transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>{addButtonLabel}</span>
            </button>
            <button
              onClick={deleteLastSection}
              className="bg-gray-600 hover:bg-gray-700 text-white text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-lg inline-flex items-center gap-1.5 whitespace-nowrap transition-colors"
              disabled={sections.length === 0}
            >
              <Minus className="w-4 h-4" />
              <span>{deleteButtonLabel}</span>
            </button>
          </div>

          {sections.length === 0 && (
            <div className="bg-black/40 border border-white/20 rounded-lg p-6 text-center">
              <div className="text-sm text-slate-400 mb-2">{emptyLabel}</div>
              <div className="text-xs text-slate-500">{emptySubLabel}</div>
            </div>
          )}

          {sections.length > 0 && (
            <div className="bg-black/40 border border-white/20 rounded-lg p-2 sm:p-4 space-y-2 max-h-[500px] overflow-y-auto">
              {sections.map((s) => (
                <div key={s.id} className="flex items-center gap-2 text-white py-2.5 px-2.5 sm:py-3 sm:px-3 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-colors">
                  {/* min-w-0 is what lets this shrink. Without it the row refused
                      to give up any width and pushed Remove off the screen. */}
                  <button
                    onClick={() => handleEditExistingSection(s.id)}
                    className="text-left hover:text-white/90 min-w-0 flex-1 flex items-start gap-3"
                  >
                    {(() => {
                      const Icon = resolveBlockIcon(s, { isExample })
                      return (
                        <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/5 text-purple-300">
                          <Icon className="h-4 w-4" />
                        </span>
                      )
                    })()}
                    <span className="min-w-0 flex-1">
                    <div className="font-medium mb-1 text-sm sm:text-base">{s.title || "Untitled"}</div>
                    <div className="text-xs text-gray-300 max-h-12 overflow-hidden">
                      {s.markdown || s.content || s.html ? (
                        <div className="line-clamp-2 text-ellipsis">
                          {(s.markdown || s.content || s.html).substring(0, 150)}
                          {(s.markdown || s.content || s.html).length > 150 ? '...' : ''}
                        </div>
                      ) : (
                        <span className="text-gray-500 italic">Empty section</span>
                      )}
                    </div>
                    </span>
                  </button>
                  {/* Icon-only below sm — the word does not fit next to a title. */}
                  <button
                    onClick={() => removeSection(s.id)}
                    aria-label="Remove"
                    title="Remove"
                    className="flex-shrink-0 text-xs text-red-300 hover:text-red-200 p-1.5 sm:px-2 sm:py-1 rounded hover:bg-red-900/20 transition-colors"
                  >
                    <Trash2 className="h-4 w-4 sm:hidden" />
                    <span className="hidden sm:inline">Remove</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Overlay Editor */}
      {activeOverlay && (() => {
        const currentSection = tempSection || sections.find(x => x.id === activeOverlay)
        if (!currentSection) return null
        
          const overlay = (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
              <div className="w-full max-w-[700px] h-[80vh] bg-black/90 border border-white/20 rounded-2xl shadow-2xl flex flex-col mx-4">
                {/* Header (icono + título input) */}
                <div className="flex items-start justify-between px-6 py-4 border-b border-white/10">
                  <div className="flex-1 mr-3">
                    <div className="flex items-start gap-2">
                      <IconPicker
                        value={currentSection.icon || ""}
                        onChange={(icon) => updateTempSection({ icon })}
                        name={currentSection.title}
                        groups={ICON_GROUPS}
                        suggest={suggestIconForName}
                        neutralIcon={resolveBlockIcon({ icon: null }, { isExample })}
                        emptyHint="No icon picked — this block shows a neutral one."
                      />
                      <input
                        type="text"
                        value={currentSection.title ?? ""}
                        onChange={(e) => updateTempSection({ title: e.target.value })}
                        placeholder={autoTitle ? "Title will be generated automatically" : "Enter section title..."}
                        className={`flex-1 h-10 bg-white/10 border border-white/20 rounded-lg px-3 text-white text-lg font-medium focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 placeholder-gray-400 ${
                          autoTitle ? 'cursor-not-allowed opacity-70' : ''
                        }`}
                        autoFocus={!autoTitle}
                        readOnly={autoTitle}
                      />
                    </div>
                    {validationError && (
                      <p className="text-red-400 text-sm mt-2">{validationError}</p>
                    )}
                  </div>
                  <button
                    onClick={handleCloseOverlay}
                    className="w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-white/10"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Editor centrado */}
                <div className="flex-1 overflow-auto p-6">
                  <MarkdownToolbarEditor
                    value={currentSection.html}
                    onChange={({ html, markdown }) =>
                      updateTempSection({ html, markdown, content: markdown })
                    }
                    toolbarClassName="sticky top-0 z-10 mb-4"
                    className="relative"
                    editorMinHeight="h-[500px] pt-4 px-4"
                    placeholder={placeholder}
                  />
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-white/10 bg-black/70 flex justify-end">
                  <button
                    onClick={handleSaveSection}
                    className="text-white text-sm px-4 py-2 bg-primary hover:bg-violet-700 rounded-lg transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          )

          // Render overlay into document.body to avoid stacking/overflow issues in parent containers
          if (typeof document !== "undefined") {
            return createPortal(overlay, document.body)
          }

          return overlay
      })()}
    </div>
  )
}